import { authHeaders, fetchWithAuthRetry, getApiUrl, parseApiError } from './common';
import type { MeFeedAuthor, MeFeedEntityType, MeFeedItem, MePagedFeed } from './me';

/**
 * 홈 통합 피드 read-only GraphQL client (Mercurius 스파이크).
 *
 * `MePagedFeed`로 정규화해 반환하므로 호출부/UI 모델은 동일하다.
 * 홈 피드의 단일 transport이며 `useHomeFeed`가 이를 사용한다.
 */

const FEED_QUERY = `
  query HomeFeed(
    $limit: Int
    $cursor: String
    $entity: [FeedEntityType!]
    $author: FeedAuthor
    $projectIds: [ID!]
    $planStatus: [String!]
    $reportStatus: [String!]
    $postMortemStatus: [String!]
    $coActionStatus: [String!]
    $codeReviewStatus: [String!]
  ) {
    feed(
      limit: $limit
      cursor: $cursor
      entity: $entity
      author: $author
      projectIds: $projectIds
      planStatus: $planStatus
      reportStatus: $reportStatus
      postMortemStatus: $postMortemStatus
      coActionStatus: $coActionStatus
      codeReviewStatus: $codeReviewStatus
    ) {
      nextCursor
      nodes {
        id
        entityType
        title
        status
        sessionStatus
        qualityScore
        projectId
        projectName
        teamId
        createdByName
        occurredAt
        archivedAt
        priority
        type
        linkedReportId
        linkedReportCount
        linkedReportQualityScore
        linkedReportReviewRecommendation
        linkedReportReviewReason
        linkedReportReviewStatus
        linkedPostMortemId
        linkedPostMortemCount
        linkedCodeReviewId
        linkedCodeReviewCount
      }
    }
  }
`;

type FeedGraphqlNode = {
  id: string;
  entityType: MeFeedEntityType;
  title: string;
  status: string;
  sessionStatus: MeFeedItem['sessionStatus'];
  qualityScore: number | null;
  projectId: string;
  projectName: string | null;
  teamId: string | null;
  createdByName: string | null;
  occurredAt: string;
  archivedAt: string | null;
  priority: string | null;
  type: string | null;
  linkedReportId: string | null;
  linkedReportCount: number;
  linkedReportQualityScore: number | null;
  linkedReportReviewRecommendation: string | null;
  linkedReportReviewReason: string | null;
  linkedReportReviewStatus: string | null;
  linkedPostMortemId: string | null;
  linkedPostMortemCount: number;
  linkedCodeReviewId: string | null;
  linkedCodeReviewCount: number;
};

type FeedGraphqlPayload = {
  data?: { feed: { nextCursor: string | null; nodes: FeedGraphqlNode[] } } | null;
  errors?: Array<{ message: string }>;
};

const omitNullish = (input: Record<string, unknown>): Record<string, unknown> => {
  const out: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(input)) {
    if (value === undefined || value === null) continue;
    if (Array.isArray(value) && value.length === 0) continue;
    out[key] = value;
  }
  return out;
};

export const fetchMeFeedGraphql = async (
  token: string,
  opts: {
    cursor?: string | null;
    limit?: number;
    entity?: MeFeedEntityType[];
    author?: MeFeedAuthor;
    planStatus?: string[];
    reportStatus?: string[];
    postMortemStatus?: string[];
    coActionStatus?: string[];
    codeReviewStatus?: string[];
    projectIds?: string[];
  } = {},
): Promise<MePagedFeed> => {
  const url = `${getApiUrl()}/api/graphql`;
  const variables = omitNullish({
    limit: opts.limit,
    cursor: opts.cursor,
    entity: opts.entity,
    author: opts.author,
    projectIds: opts.projectIds,
    planStatus: opts.planStatus,
    reportStatus: opts.reportStatus,
    postMortemStatus: opts.postMortemStatus,
    coActionStatus: opts.coActionStatus,
    codeReviewStatus: opts.codeReviewStatus,
  });

  const response = await fetchWithAuthRetry(url, {
    method: 'POST',
    cache: 'no-store',
    headers: authHeaders(token, true),
    body: JSON.stringify({ query: FEED_QUERY, variables }),
  });

  if (!response.ok) {
    throw await parseApiError(response, `Failed to fetch home feed: ${response.status}`);
  }

  const payload = (await response.json()) as FeedGraphqlPayload;
  if (payload.errors && payload.errors.length > 0) {
    throw new Error(payload.errors[0]?.message ?? 'GraphQL feed failed');
  }
  const feed = payload.data?.feed;
  if (!feed) {
    throw new Error('GraphQL feed returned no data');
  }

  return {
    data: feed.nodes.map((node): MeFeedItem => ({ ...node })),
    nextCursor: feed.nextCursor,
  };
};
